<template>
    <div class="block block-image-gallery">
        <div class="container-fluid" v-if="block.sectionHeading">
            <h2 class="section-heading">{{ block.sectionHeading }}</h2>
        </div>
        <div v-if="block.images.length" class="gallery-wrapper">
            <div class="gallery-scroll-container" ref="scrollContainer">
                <div class="gallery-images" ref="galleryImages">
                    <figure v-for="(image, index) in block.images" :key="'image-' + index"
                        class="gallery-image-wrapper">
                        <picture>
                            <source media="(max-width: 575px)" :srcset="image.srcs.small.src" />
                            <source media="(max-width: 768px)" :srcset="image.srcs.small.src" />
                            <img :src="image.srcs.large.src" :width="image.srcs.large.width"
                                :height="image.srcs.large.height" :alt="image.title" class="gallery-image" />
                        </picture>
                        <div class="gallery-image-overlay" :class="{ 'active': activeCaptions[index] }">
                            <div class="gallery-caption" v-if="image.caption" v-html="image.caption"></div>
                        </div>
                        <button v-if="image.caption" class="gallery-info-icon" @click.stop="toggleCaption(index)"
                            :aria-label="'Show caption for ' + (image.title || 'image')">
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none"
                                viewBox="0 0 25 25">
                                <rect width="24" height="24" x=".5" y=".5" stroke="#fff" rx="12" />
                                <path fill="#fff"
                                    d="M11.837 17.5V9.132h1.328V17.5zm.64-10.112c-.48 0-.88-.4-.88-.88s.4-.88.88-.88.88.4.88.88-.4.88-.88.88" />
                            </svg>
                        </button>
                    </figure>
                </div>
            </div>
            <div class="gallery-scrollbar" ref="scrollbar">
                <div class="gallery-scrollbar-track" ref="scrollbarTrack" @mousedown="onTrackClick">
                    <div class="gallery-scrollbar-thumb" ref="scrollbarThumb"
                        :style="{ width: scrollbarWidth + '%', left: scrollbarPosition + '%' }"
                        @mousedown="onThumbMouseDown"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'imageGallery',
    props: ['block', 'themeColor'],
    data() {
        return {
            activeCaptions: {},
            scrollbarWidth: 0,
            scrollbarPosition: 0,
            isDragging: false,
            dragStartX: 0,
            dragOffsetX: 0, // Offset between mouse/touch and thumb center
            dragStartScrollLeft: 0,
            lastDragX: 0,
            lastDragTime: 0,
            velocity: 0,
            momentumAnimation: null,
            resizeObserver: null,
            resizeTimeout: null,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.waitForImagesAndUpdate();
            if (this.$refs.scrollContainer) {
                this.$refs.scrollContainer.addEventListener('scroll', this.updateScrollbar);
                window.addEventListener('resize', this.updateScrollbar);
                
                // Use ResizeObserver to detect layout changes (important for production builds)
                if (window.ResizeObserver && this.$refs.galleryImages) {
                    this.resizeObserver = new ResizeObserver(() => {
                        // Debounce resize observer updates
                        if (this.resizeTimeout) {
                            clearTimeout(this.resizeTimeout);
                        }
                        this.resizeTimeout = setTimeout(() => {
                            this.updateScrollbar();
                        }, 50);
                    });
                    this.resizeObserver.observe(this.$refs.galleryImages);
                }
            }
        });
    },
    updated() {
        // Recalculate scrollbar when component updates (e.g., route changes)
        // Use multiple animation frames to ensure layout is fully settled in production builds
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.waitForImagesAndUpdate();
                });
            });
        });
    },
    beforeUnmount() {
        if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.removeEventListener('scroll', this.updateScrollbar);
            window.removeEventListener('resize', this.updateScrollbar);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.removeDragListeners();
        if (this.momentumAnimation) {
            cancelAnimationFrame(this.momentumAnimation);
        }
    },
    methods: {
        toggleCaption(index) {
            const currentValue = this.activeCaptions[index] || false;
            this.activeCaptions[index] = !currentValue;
        },
        waitForImagesAndUpdate() {
            if (!this.$refs.galleryImages) {
                return;
            }
            
            const images = this.$refs.galleryImages.querySelectorAll('img');
            if (images.length === 0) {
                // Wait for layout to settle with multiple animation frames
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                    });
                });
                return;
            }
            
            let loadedCount = 0;
            const totalImages = images.length;
            let allImagesLoaded = false;
            
            const checkAndUpdate = () => {
                if (allImagesLoaded) {
                    // Use multiple animation frames to ensure layout is fully calculated
                    // This is especially important in production builds where timing differs
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            this.updateScrollbar();
                        });
                    });
                }
            };
            
            // Check if images are already loaded
            images.forEach((img) => {
                if (img.complete && img.naturalHeight !== 0) {
                    loadedCount++;
                } else {
                    img.addEventListener('load', () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            allImagesLoaded = true;
                            checkAndUpdate();
                        }
                    }, { once: true });
                    
                    img.addEventListener('error', () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            allImagesLoaded = true;
                            checkAndUpdate();
                        }
                    }, { once: true });
                }
            });
            
            // If all images are already loaded, update after layout settles
            if (loadedCount === totalImages) {
                allImagesLoaded = true;
                checkAndUpdate();
            } else {
                // Fallback: update after a delay even if not all images loaded
                // This handles cases where images fail to fire load events
                setTimeout(() => {
                    if (!allImagesLoaded) {
                        allImagesLoaded = true;
                        checkAndUpdate();
                    }
                }, 300);
            }
        },
        updateScrollbar() {
            if (!this.$refs.scrollContainer || !this.$refs.galleryImages) {
                return;
            }
            const container = this.$refs.scrollContainer;
            const content = this.$refs.galleryImages;
            const scrollWidth = content.scrollWidth;
            const clientWidth = container.clientWidth;
            
            // Prevent division by zero and invalid calculations
            if (scrollWidth <= 0 || clientWidth <= 0) {
                return;
            }
            
            // Only update if scrollWidth is actually larger than clientWidth
            // This prevents the scrollbar from filling the whole width incorrectly
            if (scrollWidth <= clientWidth) {
                this.scrollbarWidth = 0;
                this.scrollbarPosition = 0;
                return;
            }
            
            const scrollLeft = container.scrollLeft;
            this.scrollbarWidth = (clientWidth / scrollWidth) * 100;
            const maxScroll = scrollWidth - clientWidth;
            this.scrollbarPosition = maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - this.scrollbarWidth) : 0;
        },
        onThumbMouseDown(e) {
            e.preventDefault();
            e.stopPropagation();
            this.isDragging = true;

            // Calculate the offset between mouse position and thumb position
            const thumb = this.$refs.scrollbarThumb;
            const thumbRect = thumb.getBoundingClientRect();
            this.dragStartX = e.clientX;
            this.dragOffsetX = e.clientX - thumbRect.left - (thumbRect.width / 2); // Offset from center of thumb

            this.lastDragX = e.clientX;
            this.lastDragTime = Date.now();
            this.velocity = 0;
            this.dragStartScrollLeft = this.$refs.scrollContainer.scrollLeft;
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.onDragEnd);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        },
        onTrackClick(e) {
            if (e.target === this.$refs.scrollbarThumb) {
                return;
            }
            if (!this.$refs.scrollbarTrack || !this.$refs.scrollContainer || !this.$refs.galleryImages) {
                return;
            }
            const track = this.$refs.scrollbarTrack;
            const trackRect = track.getBoundingClientRect();
            const clickX = e.clientX - trackRect.left;
            const trackWidth = trackRect.width;
            const percentage = clickX / trackWidth;

            const container = this.$refs.scrollContainer;
            const content = this.$refs.galleryImages;
            const scrollWidth = content.scrollWidth;
            const clientWidth = container.clientWidth;
            const maxScroll = scrollWidth - clientWidth;

            container.scrollLeft = percentage * maxScroll;
        },
        onDrag(e) {
            if (!this.isDragging || !this.$refs.scrollContainer || !this.$refs.scrollbarTrack) {
                return;
            }
            e.preventDefault();
            const now = Date.now();
            const currentX = e.clientX;

            // Calculate velocity for momentum
            if (this.lastDragTime > 0) {
                const timeDelta = now - this.lastDragTime;
                const distanceDelta = currentX - this.lastDragX;
                if (timeDelta > 0) {
                    this.velocity = distanceDelta / timeDelta;
                }
            }
            this.lastDragX = currentX;
            this.lastDragTime = now;

            // Calculate position based on mouse position relative to track, accounting for thumb offset
            const track = this.$refs.scrollbarTrack;
            const trackRect = track.getBoundingClientRect();
            const thumb = this.$refs.scrollbarThumb;
            const thumbWidth = thumb.getBoundingClientRect().width;

            // Mouse position relative to track
            const mouseXRelativeToTrack = currentX - trackRect.left;

            // Calculate where the thumb center should be (mouse position minus the offset from when we grabbed it)
            let thumbCenterX = mouseXRelativeToTrack - this.dragOffsetX;

            // Constrain thumb center to stay within bounds (accounting for thumb width)
            const minCenterX = thumbWidth / 2;
            const maxCenterX = trackRect.width - (thumbWidth / 2);
            thumbCenterX = Math.max(minCenterX, Math.min(maxCenterX, thumbCenterX));

            // Convert to percentage (0 to 1)
            const percentage = (thumbCenterX - minCenterX) / (maxCenterX - minCenterX);

            const container = this.$refs.scrollContainer;
            const content = this.$refs.galleryImages;
            const scrollWidth = content.scrollWidth;
            const clientWidth = container.clientWidth;
            const maxScroll = scrollWidth - clientWidth;

            container.scrollLeft = percentage * maxScroll;
        },
        onDragEnd() {
            this.isDragging = false;
            this.removeDragListeners();
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            // Apply momentum scrolling if velocity is significant
            if (Math.abs(this.velocity) > 0.1 && this.$refs.scrollContainer && this.$refs.scrollbarTrack) {
                this.applyMomentum();
            }

            // Reset velocity tracking
            this.lastDragTime = 0;
            this.lastDragX = 0;
        },
        applyMomentum() {
            if (!this.$refs.scrollContainer || !this.$refs.galleryImages || !this.$refs.scrollbarTrack) {
                return;
            }

            const container = this.$refs.scrollContainer;
            const content = this.$refs.galleryImages;
            const track = this.$refs.scrollbarTrack;
            const trackWidth = track.getBoundingClientRect().width;
            const scrollWidth = content.scrollWidth;
            const clientWidth = container.clientWidth;
            const maxScroll = scrollWidth - clientWidth;

            // Convert velocity from pixels/ms to scroll units
            const scrollVelocity = (this.velocity / trackWidth) * maxScroll;

            // Momentum parameters
            const friction = 0.95; // How quickly it slows down
            const minVelocity = 0.5; // Stop when velocity is below this

            let currentVelocity = scrollVelocity;
            let startTime = Date.now();

            const animate = () => {
                if (Math.abs(currentVelocity) < minVelocity) {
                    if (this.momentumAnimation) {
                        cancelAnimationFrame(this.momentumAnimation);
                        this.momentumAnimation = null;
                    }
                    return;
                }

                const now = Date.now();
                const deltaTime = now - startTime;
                startTime = now;

                // Calculate distance to scroll based on velocity and time
                const scrollDelta = currentVelocity * (deltaTime / 16); // Normalize to ~60fps
                const currentScroll = container.scrollLeft;
                const newScroll = currentScroll + scrollDelta;

                // Apply scroll with bounds checking
                if (newScroll <= 0) {
                    container.scrollLeft = 0;
                    if (this.momentumAnimation) {
                        cancelAnimationFrame(this.momentumAnimation);
                        this.momentumAnimation = null;
                    }
                    return;
                } else if (newScroll >= maxScroll) {
                    container.scrollLeft = maxScroll;
                    if (this.momentumAnimation) {
                        cancelAnimationFrame(this.momentumAnimation);
                        this.momentumAnimation = null;
                    }
                    return;
                } else {
                    container.scrollLeft = newScroll;
                }

                // Apply friction
                currentVelocity *= friction;

                // Continue animation
                this.momentumAnimation = requestAnimationFrame(animate);
            };

            // Cancel any existing momentum animation
            if (this.momentumAnimation) {
                cancelAnimationFrame(this.momentumAnimation);
            }

            this.momentumAnimation = requestAnimationFrame(animate);
        },
        removeDragListeners() {
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.onDragEnd);
        },
    },
};
</script>
